import type { AsyncList } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { useCallback, useMemo, useRef, useState } from "react";

export interface AsyncListConfig<T> {
    /** A short label describing the list items. Will be used to correlate any query events associated with the select */
    name: string;
    /** Gets the ID of an item */
    getItemId: (item: T) => string;
    /** A function that loads the list items asynchronously */
    loadItems: AsyncListItemsLoader<T>;
    /** Any items that need to be made available before any loading has been done (e.g. a selected value) can be added here */
    initialItems: T[];
    /**
     * If the loading of the list has been started elsewhere, the query promise can be passed in here.
     * This query must attempt to load take + 1 items, to enable detecting whether there are more items to be loaded.
     */
    initialQuery?: Promise<T[]>;
    /** The number of additional items to load for each increment */
    take?: number;
}

export function useAsyncList<T>(config: AsyncListConfig<T>): AsyncList<T> {
    const configRef = useRef(config); // Config ref is used in queries to avoid re-querying when loadItems changes
    configRef.current = config;

    const [itemsById] = useState(() => new Map(config.initialItems.map((item) => [config.getItemId(item), item])));

    const takeIncrement = config.take ?? defaultAsyncListTake;
    const [take, setTake] = useState(takeIncrement);
    const [filterText, setFilterText] = useState("");
    const [filterTake, setFilterTake] = useState(takeIncrement);

    const isFiltering = filterText !== "";

    const handleListItemsLoaded = useCallback(
        (items: T[], take: number) => {
            items.forEach((item) => itemsById.set(configRef.current.getItemId(item), item));

            return {
                // we attempted to load take + 1 items, if it successfully loaded more than take then hasMore will be set to true
                items: items.slice(0, take),
                hasMore: items.length > take,
            };
        },
        [itemsById]
    );

    const initialQuery = config.initialQuery?.then((items) => handleListItemsLoaded(items, take));

    const query = useInlineStatusQuery(
        async (repository: Repository) => {
            // We load take + 1 items so that we can determine whether there are more items to be loaded
            const items = await configRef.current.loadItems(repository, take + 1, "");
            return handleListItemsLoaded(items, take);
        },
        [take, handleListItemsLoaded],
        `Async List - ${config.name}`,
        {
            initialQuery,
        }
    );

    const filterQuery = useInlineStatusQuery(
        async (repository: Repository) => {
            // Ideally we shouldn't run this query at all when not filtering, but infrastructure doesn't support this
            if (!isFiltering) {
                return {
                    ...query.result,
                    filterText,
                };
            }

            // We load take + 1 items so that we can determine whether there are more items to be loaded
            const items = await configRef.current.loadItems(repository, filterTake + 1, filterText);
            return {
                ...handleListItemsLoaded(items, filterTake),
                filterText,
            };
        },
        [isFiltering, filterTake, filterText, handleListItemsLoaded, query.result],
        `Async List - ${config.name} (filtered)`,
        {
            // A hack to force this query not to execute initially
            initialResult: { items: [], hasMore: true, filterText: "" },
        }
    );

    const loadedItems = useMemo(() => {
        if (isFiltering) {
            return filterText !== filterQuery.result?.filterText && filterQuery.isLoading ? [] : (filterQuery.result.items ?? []);
        }

        return query.result?.items ?? [];
    }, [isFiltering, filterText, filterQuery.result?.filterText, filterQuery.result.items, filterQuery.isLoading, query.result?.items]);

    const getItemById = useCallback((id: string) => itemsById.get(id), [itemsById]);
    const loadMore = query.isLoading ? undefined : query.result?.hasMore ? () => setTake((i) => i + takeIncrement) : undefined;
    const loadMoreFiltered = filterQuery.result?.hasMore ? () => setFilterTake((i) => i + takeIncrement) : undefined;

    return {
        getItemId: config.getItemId,
        getItemById,
        loadedItems,
        isLoading: isFiltering ? filterQuery.isLoading : query.isLoading,
        error: isFiltering ? filterQuery.error : query.error,
        loadMore: isFiltering ? loadMoreFiltered : loadMore,
        setFilterText: (text: string) => {
            setFilterText(text);
            setFilterTake(takeIncrement);
        },
    };
}

export type AsyncListItemsLoader<T> = (repository: Repository, take: number, filterText: string) => Promise<T[]>;

export const defaultAsyncListTake = 30;

export type CreateAsyncListConfig<T> = Omit<AsyncListConfig<T>, "initialQuery">;

export function createAsyncList<T>(repository: Repository, config: CreateAsyncListConfig<T>): AsyncListConfig<T> {
    const take = config.take ?? defaultAsyncListTake;
    const initialQuery = config.loadItems(repository, take + 1, "");
    return {
        ...config,
        initialQuery,
    };
}
